<script setup lang="ts">
// InviteResultList — the per-email outcome screen (v3 `UserPermissionModal.vue:184-238`).
//
// ⚠ THREE STATUSES, and the middle one is the reason this component exists. `already_invited` is
// neither success nor failure: legacy commit `3152f6e0` was written because the screen had no
// branch for it and therefore rendered a green check AND a red "Failed" chip for the same row,
// with the server's reason suppressed. It gets its own icon, its own neutral tone and its own
// term here — see `models/memberInvite.types.ts` for why both the contract and the ticket say
// otherwise.
//
// v3 achieved this screen by declaring a SECOND `#body` slot (`:7` and `:184`) and relying on the
// later duplicate key overwriting the earlier one in the compiled slots object. That is not a
// mechanism, it is a coincidence of the compiler: rendered as two sibling blocks under one slot,
// the form and the results would both appear at once. `MemberInviteModal` switches on `submitted`
// instead, so only one is ever mounted.
import { computed } from 'vue'

import type { InviteResultDto, InviteResultStatus } from '../../models/memberInvite.types'
import { summarizeInvites } from '../../service/inviteResults'

import { useI18n } from '@/shared/i18n'
import { Badge, Icon, type IconName, type UiTone } from '@/shared/ui'

const props = defineProps<{ results: readonly InviteResultDto[] }>()

const { t } = useI18n()

const STATUS_ICON: Readonly<Record<InviteResultStatus, IconName>> = {
  success: 'check',
  already_invited: 'info',
  error: 'x',
}

const STATUS_TONE: Readonly<Record<InviteResultStatus, UiTone>> = {
  success: 'success',
  already_invited: 'neutral',
  error: 'danger',
}

const STATUS_LABEL_KEY: Readonly<Record<InviteResultStatus, string>> = {
  success: 'workspaceSettings.permissions.invite.status.invited',
  already_invited: 'workspaceSettings.permissions.invite.status.alreadyInvited',
  error: 'workspaceSettings.permissions.invite.status.failed',
}

const summary = computed(() => summarizeInvites(props.results))

/** v3's `resultSummary` (`:354-362`): only the non-zero counts, joined. */
const summaryLine = computed(() =>
  [
    summary.value.invited > 0 &&
      t('workspaceSettings.permissions.invite.summary.invited', { count: summary.value.invited }),
    summary.value.already > 0 &&
      t('workspaceSettings.permissions.invite.summary.already', { count: summary.value.already }),
    summary.value.failed > 0 &&
      t('workspaceSettings.permissions.invite.summary.failed', { count: summary.value.failed }),
  ]
    .filter((part): part is string => typeof part === 'string')
    .join(' · '),
)
</script>

<template>
  <div class="invite-results flex flex-col gap-md font-sans" data-testid="invite-results">
    <header class="invite-results__header flex flex-col items-center gap-2xs text-center">
      <Icon
        :name="summary.allInvited ? 'check' : 'alert-triangle'"
        :size="20"
        :class="summary.allInvited ? 'invite-results__mark--ok' : 'invite-results__mark--warn'"
      />
      <p class="invite-results__title m-0 text-[0.9375rem] font-semibold">
        {{ t('workspaceSettings.permissions.invite.processed') }}
      </p>
      <p
        class="invite-results__summary m-0 text-muted text-[0.8125rem]"
        data-testid="invite-results-summary"
      >
        {{ summaryLine }}
      </p>
    </header>

    <ul
      class="invite-results__list flex flex-col m-0 p-0 border border-border rounded-md list-none"
      role="list"
    >
      <li
        v-for="(result, index) in props.results"
        :key="`${index}-${result.email}`"
        class="invite-results__row flex items-center gap-xs py-xs px-sm border-b border-b-border text-[0.8125rem] [&:last-child]:border-b-0"
        :data-status="result.status"
      >
        <Icon :name="STATUS_ICON[result.status]" :size="14" />
        <span
          class="invite-results__email flex-auto min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
          >{{ result.email }}</span
        >
        <!-- The backend's reason, shown for ANY status that carries one. v3 gated this on
             `status === 'error'`, which `already_invited` can never satisfy. -->
        <span
          v-if="result.error"
          class="invite-results__reason flex-[0_1_auto] max-w-48 overflow-hidden text-muted text-[0.6875rem] text-ellipsis whitespace-nowrap"
          :title="result.error"
        >
          {{ result.error }}
        </span>
        <Badge :tone="STATUS_TONE[result.status]" size="sm">
          {{ t(STATUS_LABEL_KEY[result.status]) }}
        </Badge>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.invite-results .invite-results__mark--ok {
  color: var(--ui-color-success);
}
.invite-results .invite-results__mark--warn {
  color: var(--ui-color-warning);
}
</style>
